import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AllGamesList from './components/AllGamesList';
import Home from './components/Home';
import Stats from './components/Stats';
import Login from './components/Login';
import './App.css';
import EditScoreForm from './components/EditScoreForm';
import Signup from './components/Signup';
import ResetPassword from './components/ResetPassword';
import MainLayout from './components/MainLayout';
import About from './components/About';
import OAuthCallback from './components/OAuthCallback';
import ShareLander from './components/ShareLander';
import { useAuth } from './contexts/AuthContext';
import { usePlay } from './contexts/PlayContext';
import { useInviteAcceptance } from './hooks/useInviteAcceptance';
import { games } from './data/games';
import { useState, useEffect, useCallback, useRef } from 'react';
import { debugLog, debugWarn, debugError, debugSuccess } from './utils/debugLogger';
import { initAnalytics } from './services/analyticsService';

console.log("NLG v" + require('../package.json').version);

// How many times an account has to have clicked through to a game before
// returning from one offers the score form on its own.
//
// It is NOT about having an account - a guest token is fine, and always has
// been (D22). It's about not ambushing someone who's still deciding whether
// they like the site: being redirected to a form the first time you try a
// puzzle asks for a commitment you haven't made yet. The pencil in My Games
// is there from the very first play for anyone who does want to log a score.
//
// Counted server-side as raw click-throughs, repeats included - see
// `gameClickCount` in plays-update. Accounts that predate the counter start at
// zero, so existing users have to re-earn it.
const AUTO_SCORE_MIN_GAME_CLICKS = 15;

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, isAuthenticated } = useAuth();
  const {
    checkForRecentGameVisit,
    clearRecentGameVisit,
    loadFriendsData,
    getFriendsDataFetchedAt
  } = usePlay();

  // Several signals can announce a return from a game at once - a route
  // change, window focus, visibilitychange, pageshow - and each used to start
  // its own dashboard read. One run at a time.
  const autoRedirectRunning = useRef(false);

  // Connects a visitor who arrived through a share link, the moment they first
  // have a record of their own - however that happened (D48). Lives here
  // rather than on the lander because the record usually comes into being
  // after they've navigated away from it.
  useInviteAcceptance();

  // Track page load with auth state once loading completes
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  useEffect(() => {
    if (!isLoading && !analyticsInitialized) {
      initAnalytics(isAuthenticated, user?.id || null);
      setAnalyticsInitialized(true);
    }
  }, [isLoading, isAuthenticated, user, analyticsInitialized]);

  // Shared function to check for recent game visits and auto-redirect to score entry
  const checkAutoRedirect = useCallback(async () => {
    debugLog('checkAutoRedirect called');

    // No account needed any more (D22): everyone can log a score, so this runs
    // for everyone. It's still gated on having written *something* - with no
    // record there's no play history to check against.
    if (!user) {
      debugLog('No user record yet, skipping');
      return;
    }
    if (isLoading) {
      debugLog('Still loading, skipping');
      return;
    }
    if (location.pathname.includes('/edit-score')) {
      debugLog('Already on edit-score page, skipping');
      return;
    }

    const recentVisit = checkForRecentGameVisit();
    if (!recentVisit) return;

    if (autoRedirectRunning.current) {
      debugLog('Auto-redirect check already in progress, skipping');
      return;
    }
    autoRedirectRunning.current = true;

    try {
      debugLog(`Recent visit found for game ${recentVisit.gameId}`);

      // Find the game to check if it has a scoreType
      const game = games.find(g => g.id.toString() === recentVisit.gameId.toString());

      // Each of these is a settled "no", not a "not yet" - so the marker goes,
      // rather than being re-examined (and re-fetched against) on every route
      // change and every tab focus for the rest of its ten minutes.
      if (!game) {
        debugWarn(`Game ${recentVisit.gameId} not found in games list`);
        clearRecentGameVisit();
        return;
      }
      if (!game.scoreType) {
        debugLog(`Game ${game.name} has no scoreType, skipping`);
        clearRecentGameVisit();
        return;
      }

      // The dashboard already in hand is good enough unless it was read before
      // the game was opened - only then could it be missing a score the game
      // posted back while the user was away. On an ordinary return the load
      // this page did on mount is already newer, so this joins it instead of
      // waiting on a second cold request.
      const needsFreshRead = getFriendsDataFetchedAt() < recentVisit.clickTime;
      debugLog(`Checking for an existing score (fresh read: ${needsFreshRead})`);
      const freshData = await loadFriendsData(needsFreshRead);

      // Not yet a regular - leave them alone. Read off the response rather than
      // context state, same as the score check below, since this runs before
      // React has re-rendered with the new data.
      const gameClicks = freshData?.user?.gameClickCount ?? 0;
      if (gameClicks < AUTO_SCORE_MIN_GAME_CLICKS) {
        debugLog(`Only ${gameClicks} game clicks so far, below the ${AUTO_SCORE_MIN_GAME_CLICKS} needed to auto-prompt`);
        clearRecentGameVisit();
        return;
      }

      const todayPlay = freshData?.user?.today_plays?.[recentVisit.gameId];
      const hasScore = todayPlay && todayPlay.score !== null && todayPlay.score !== '';
      if (!hasScore) {
        debugSuccess(`No score found! Redirecting to score entry for ${game.name}`);
        // Redirect to score entry form. No `from` state - the form always
        // returns to Home now (4.8), on both save and cancel. The marker is
        // left in place for the form itself to clear on save or cancel.
        navigate(`/edit-score/${recentVisit.gameId}`);
      } else {
        debugLog(`User already has score for ${game.name} today`);
        clearRecentGameVisit();
      }
    } catch (error) {
      // Deliberately keeps the marker: a failed read is a "don't know", and the
      // next focus or route change should get to try again.
      debugError(`Error in checkAutoRedirect: ${error.message}`);
      console.error('Error checking for auto-redirect:', error);
    } finally {
      autoRedirectRunning.current = false;
    }
  }, [
    user,
    isLoading,
    location.pathname,
    checkForRecentGameVisit,
    clearRecentGameVisit,
    loadFriendsData,
    getFriendsDataFetchedAt,
    navigate
  ]);

  // Check for auto-redirect on route changes
  useEffect(() => {
    checkAutoRedirect();
  }, [checkAutoRedirect, location.key]);

  // Jump to the top of the page on every navigation, instead of preserving
  // the previous view's scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Additional effect to handle PWA focus events and ensure auto-redirect works
  // when returning to the app via X button or app switching
  useEffect(() => {
    const handleVisibilityChange = () => {
      debugLog(`visibilitychange event: hidden=${document.hidden}`);
      if (!document.hidden) {
        // Page became visible, check for auto-redirect
        checkAutoRedirect();
      }
    };

    const handleFocus = () => {
      debugLog('window focus event fired');
      checkAutoRedirect();
    };

    // Coming back from a game is a navigation to another site and back, so the
    // return is often a restore from the back/forward cache rather than a
    // fresh load: no effects re-run, and on iOS Safari neither focus nor
    // visibilitychange reliably fires if the window never lost focus. pageshow
    // does, which is what makes the prompt appear on that path at all.
    const handlePageShow = (event) => {
      debugLog(`pageshow event: persisted=${event.persisted}`);
      checkAutoRedirect();
    };

    // Listen for window focus events (when user returns to the app)
    window.addEventListener('focus', handleFocus);

    // Listen for visibility change events (when tab/app becomes visible)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.addEventListener('pageshow', handlePageShow);

    debugLog('Event listeners registered for focus, visibilitychange and pageshow');

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [checkAutoRedirect]);

  // Don't show loading screen on auth pages (they handle their own loading states)
  const isAuthPage = ['/login', '/signup', '/reset-password'].includes(location.pathname);

  if (isLoading && !isAuthPage) {
    return (
      <div className="min-h-screen flex items-center justify-center relative z-10">
        <div className="bg-white dark:bg-black bg-opacity-70 dark:bg-opacity-50 p-6 rounded-xl shadow-xl text-center">
          <div className="loading-spinner mx-auto mb-3"></div>
          <h3 className="text-lg font-semibold mb-1 text-blue-600 dark:text-blue-200">Loading</h3>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Routes without navbar (full-page layouts) */}
      <Route path="/about" element={<About />} />

      {/* Routes with navbar (main layout) */}
      <Route element={<MainLayout centerVertically={isAuthPage} />}>
        <Route path="/" element={<Home />} />
        <Route path="/all" element={<AllGamesList />} />
        <Route path="/stats" element={<Stats />} />
        {/* Served by api/share-lander so crawlers get per-share meta tags;
            React renders the same URL for anyone with a browser (D47). */}
        <Route path="/s/:shareId" element={<ShareLander />} />
        <Route path="/favorites" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        {/* Score entry is no longer behind ProtectedRoute: under D22 the first
            score logged creates the account that stores it. */}
        <Route path="/edit-score/:gameId" element={<EditScoreForm />} />
        <Route path="/editscore/:gameId" element={<EditScoreForm />} />
      </Route>
    </Routes>
  );
}

export default App;
